import InlineFeedback from 'ai-components-ui-library/components/feedback/InlineFeedback';
import { AIBadge } from 'ai-components-ui-library/components/informative/AIBadge';
import { AILoader } from 'ai-components-ui-library/components/loading/AILoader';
import Markdown from 'ai-rendering-library/components/text/Markdown';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { ExpandableText } from 'foundations-components/transitional/components/ExpandableText';
import { useTheme } from 'foundations-theming/utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useSearchParams } from 'marketplace-ui-common/hooks/useSearchParams';
import { useTrackMarketplaceEvent, useUsageTracker } from 'marketplace-ui-tracking';
import { getMarketplaceInteractionEvent } from 'marketplace-ui-tracking/events/marketplace';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'quartz-routing/react-router';
import { stripPortalId } from '../../utils/urls';
import { useStreamAiSummary } from '../hooks/useStreamAiSummary';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FEEDBACK_HEIGHT_PX = 30;
export function AiSummaryCard({
  aiRequestId,
  gridIsSingleColumn,
  maxHeight,
  preexistingAnswer
}) {
  const theme = useTheme();
  const usageTracker = useUsageTracker();
  const trackMarketplaceEvent = useTrackMarketplaceEvent();
  const summaryRef = useRef(null);
  const history = useHistory();
  const params = useSearchParams();
  const searchTerm = params.get('search');
  const [error, setError] = useState(false);
  const handleFinish = useCallback(value => {
    if (value === 'FAILURE') setError(true);
    trackMarketplaceEvent(getMarketplaceInteractionEvent({
      action: 'ai summary streaming finished',
      search: searchTerm !== null && searchTerm !== void 0 ? searchTerm : undefined,
      value
    }));
  }, [searchTerm, trackMarketplaceEvent]);
  const streamingAnswer = useStreamAiSummary({
    onFinish: handleFinish,
    requestId: aiRequestId,
    skip: !!preexistingAnswer
  });
  const successfulAnswer = preexistingAnswer || streamingAnswer;

  // override links to use React Router, once we have a successful answer
  useEffect(() => {
    var _summaryRef$current;
    const cleanups = [];
    (_summaryRef$current = summaryRef.current) === null || _summaryRef$current === void 0 || _summaryRef$current.querySelectorAll('a').forEach(link => {
      const handler = e => {
        trackMarketplaceEvent(getMarketplaceInteractionEvent({
          action: 'ai summary card link clicked',
          value: stripPortalId(link.href)
        }));
        const path = link.href.split('/listing')[1];
        if (path) {
          e.preventDefault();
          history.push(`/listing${path}`);
        }
      };
      link.addEventListener('click', handler);
      cleanups.push(() => link.removeEventListener('click', handler));
    });
    return () => cleanups.forEach(cleanup => cleanup());
  }, [successfulAnswer, history, trackMarketplaceEvent]);
  if (successfulAnswer) {
    const paddingPx = parseInt(theme.space['600'], 10);
    const borderWidthPx = parseInt(theme.borderWidth['100'], 10);
    const computedMaxHeight = maxHeight - paddingPx * 2 - borderWidthPx * 2 - FEEDBACK_HEIGHT_PX;
    return /*#__PURE__*/_jsxs(CardContainer, {
      gridIsSingleColumn: gridIsSingleColumn,
      children: [/*#__PURE__*/_jsxs(ExpandableText, {
        maxHeight: computedMaxHeight,
        children: [/*#__PURE__*/_jsx(CardHeader, {}), /*#__PURE__*/_jsx("div", {
          ref: summaryRef,
          children: /*#__PURE__*/_jsx(Markdown, {
            html: successfulAnswer
          })
        })]
      }), /*#__PURE__*/_jsx(Flex, {
        flex: 1,
        alignItems: "end",
        children: /*#__PURE__*/_jsx(InlineFeedback, {
          messageId: aiRequestId,
          usageTracker: usageTracker,
          onNegativeFeedback: () => {
            trackMarketplaceEvent(getMarketplaceInteractionEvent({
              action: 'ai summary card negative feedback submitted'
            }));
          }
        })
      })]
    });
  }
  if (error) {
    return /*#__PURE__*/_jsxs(CardContainer, {
      gridIsSingleColumn: gridIsSingleColumn,
      children: [/*#__PURE__*/_jsx(CardHeader, {}), /*#__PURE__*/_jsx(ErrorMessage, {
        type: "badRequest"
      })]
    });
  }
  return /*#__PURE__*/_jsxs(CardContainer, {
    gridIsSingleColumn: gridIsSingleColumn,
    children: [/*#__PURE__*/_jsx(CardHeader, {}), /*#__PURE__*/_jsx(Flex, {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      paddingBlockEnd: "800",
      children: /*#__PURE__*/_jsx(AILoader, {})
    })]
  });
}
function CardContainer({
  children,
  gridIsSingleColumn
}) {
  return /*#__PURE__*/_jsx(CardWrapper, {
    compact: true,
    gridColumn: gridIsSingleColumn ? undefined : 'span 2',
    children: /*#__PURE__*/_jsx(CardSection, {
      blockSize: "100%",
      display: "flex",
      flexDirection: "column",
      children: children
    })
  });
}
function CardHeader() {
  return /*#__PURE__*/_jsxs(Flex, {
    justifyContent: "space-between",
    marginBlockEnd: "400",
    children: [/*#__PURE__*/_jsx(Text, {
      typography: "heading-25",
      textTransform: "uppercase",
      color: "text-core-subtle",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "marketplaceStorefront.searchResults.summarizedWithAi"
      })
    }), /*#__PURE__*/_jsx(AIBadge, {})]
  });
}